import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  IconButton,
  Collapse,
  Tooltip,
  useTheme,
  useMediaQuery,
  Badge,
} from '@mui/material';
import {
  Inventory,
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  AccountCircle,
  Person,
  PersonAdd,
  Logout,
  Store,
  Settings,
  CompareArrows,
  AttachMoney,
  People,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useShops } from '../../shared/contexts/ShopsContext';
import { useAuth } from '../../shared/contexts/AuthContext';

const Sidebar = ({ open, onToggle, width = 240 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { logout } = useAuth();
  const { shops } = useShops();
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    // Usunięto automatyczne zamykanie menu na urządzeniach mobilnych
    // Menu będzie teraz pozostawać otwarte na mobilnych
  }, [isMobile]);

  const menuItems = [
    {
      text: 'Zarządzanie magazynem',
      icon: <Inventory />,
      path: '/inventory',
      badge: null // Usunięto statyczną wartość 12
    },
    {
      text: 'Porównanie sklepów',
      icon: <CompareArrows />,
      path: '/comparison',
      badge: null
    },
    {
      text: 'Rozliczenia',
      icon: <AttachMoney />,
      path: '/rozliczenia',
      badge: null
    }
  ];

  const crmMenuItems = [
    {
      text: 'Klienci',
      icon: <People />,
      path: '/klienci',
      badge: null
    }
  ];

  const handleItemClick = (path) => {
    navigate(path);
    // Usunięto automatyczne zamykanie menu po kliknięciu na mobilnych
    // Menu pozostanie otwarte dla lepszej nawigacji
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const renderMenuItem = (item) => (
    <ListItem key={item.text} disablePadding>
      <Tooltip
        title={!open ? item.text : ''}
        placement="right"
        disableHoverListener={open}
      >
                 <ListItemButton
           onClick={() => handleItemClick(item.path)}
           selected={isActive(item.path)}
           sx={{
             minHeight: 48,
             justifyContent: open ? 'initial' : 'center',
             px: open ? 2.5 : 0,
             mx: open ? 1 : 0,
             borderRadius: 2,
            '&.Mui-selected': {
              backgroundColor: '#6366f1', // Primary color
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#4f46e5', // Darker primary
              },
              '& .MuiListItemIcon-root': {
                color: 'inherit',
              },
            },
            '&:hover': {
              backgroundColor: '#334155', // Dark hover
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
                                   <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 0,
                  ml: open ? 0 : 0,
                  justifyContent: 'center',
                  color: isActive(item.path) ? 'inherit' : '#94a3b8', // Light gray for inactive
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
            {item.badge ? (
              <Badge badgeContent={item.badge} color="error" max={99}>
                {item.icon}
              </Badge>
            ) : (
              item.icon
            )}
          </ListItemIcon>
          {open && (
            <ListItemText
              primary={item.text}
              sx={{
                '& .MuiListItemText-primary': {
                  fontWeight: isActive(item.path) ? 600 : 500,
                  color: isActive(item.path) ? 'inherit' : '#f8fafc',
                },
              }}
            />
          )}
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );

  const renderExpandableItem = (item, isOpen, onToggle) => (
    <ListItem key={item.text} disablePadding>
      <Tooltip
        title={!open ? item.text : ''}
        placement="right"
        disableHoverListener={open}
      >
                 <ListItemButton
           onClick={onToggle}
           sx={{
             minHeight: 48,
             justifyContent: open ? 'initial' : 'center',
             px: open ? 2.5 : 0,
             mx: open ? 1 : 0,
             borderRadius: 2,
            '&:hover': {
              backgroundColor: '#334155', // Dark hover
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
                                   <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 0,
                  ml: open ? 0 : 0,
                  justifyContent: 'center',
                  color: '#94a3b8', // Light gray
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
            {item.badge ? (
              <Badge badgeContent={item.badge} color="error" max={99}>
                {item.icon}
              </Badge>
            ) : (
              item.icon
            )}
          </ListItemIcon>
                     {open && (
             <>
               <ListItemText
                 primary={item.text}
                 sx={{
                   '& .MuiListItemText-primary': {
                     fontWeight: 500,
                     color: '#f8fafc',
                   },
                 }}
               />
               <Box sx={{ ml: 'auto' }}>
                 {isOpen ? <ExpandLess /> : <ExpandMore />}
               </Box>
             </>
           )}
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? open : true}
      onClose={isMobile ? onToggle : undefined}
      ModalProps={{
        keepMounted: true, // Lepsze dla urządzeń mobilnych
      }}
      sx={{
        width: open ? width : 64,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? width : 64,
          boxSizing: 'border-box',
          transition: 'width 0.2s ease-in-out',
          overflowX: 'hidden',
          backgroundColor: '#1e293b', // Ciemny background
          color: '#f8fafc', // Jasny tekst
          borderRight: '1px solid #334155',
          boxShadow: '4px 0 8px -1px rgb(0 0 0 / 0.1)',
          zIndex: isMobile ? 1200 : 'auto', // Wyższy z-index dla mobilnych
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          minHeight: 64,
          borderBottom: '1px solid #334155',
          backgroundColor: '#0f172a', // Ciemniejszy header
        }}
      >
        {open && (
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              fontWeight: 700,
              color: '#6366f1', // Primary color
              fontSize: '1.1rem',
              letterSpacing: '-0.025em',
            }}
          >
            Allegro Integrator
          </Typography>
        )}
        <IconButton
          onClick={onToggle}
          sx={{
            color: '#94a3b8',
            '&:hover': {
              backgroundColor: '#334155',
            },
          }}
        >
          {open ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
      </Box>

      {/* Main Navigation */}
      {open && (
        <>
          <Box sx={{ px: 2, py: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Magazyn
            </Typography>
          </Box>
          <Divider sx={{ my: 0.5, mx: 2, borderColor: '#334155' }} />
        </>
      )}
      <List sx={{ pt: 1 }}>
        {menuItems.map(renderMenuItem)}
      </List>

      {open && <Divider sx={{ my: 1, mx: 2, borderColor: '#334155' }} />}

      {/* CRM */}
      {open && (
        <>
          <Box sx={{ px: 2, py: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              CRM
            </Typography>
          </Box>
          <Divider sx={{ my: 0.5, mx: 2, borderColor: '#334155' }} />
        </>
      )}
      <List sx={{ pt: 1 }}>
        {crmMenuItems.map(renderMenuItem)}
      </List>

      {open && <Divider sx={{ my: 1, mx: 2, borderColor: '#334155' }} />}

      {/* Sklepy */}
      {open && (
        <>
          <Box sx={{ px: 2, py: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Sklepy
            </Typography>
          </Box>
          <Divider sx={{ my: 0.5, mx: 2, borderColor: '#334155' }} />
        </>
      )}
      <List>
        {shops.map(shop => (
          <ListItem key={shop.id} disablePadding>
            <Tooltip
              title={!open ? shop.name : ''}
              placement="right"
              disableHoverListener={open}
            >
                             <ListItemButton
                 onClick={() => navigate(`/shop/${shop.id}`)}
                 selected={location.pathname.startsWith(`/shop/${shop.id}`)}
                 sx={{
                   minHeight: 48,
                   justifyContent: open ? 'initial' : 'center',
                   px: open ? 2.5 : 0,
                   mx: open ? 1 : 0,
                   borderRadius: 2,
                  '&.Mui-selected': {
                    backgroundColor: '#6366f1', // Primary color
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#4f46e5', // Darker primary
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'inherit',
                    },
                  },
                  '&:hover': {
                    backgroundColor: '#334155', // Dark hover
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                                                   <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 0,
                      ml: open ? 0 : 0,
                      justifyContent: 'center',
                      color: location.pathname.startsWith(`/shop/${shop.id}`) ? 'inherit' : '#94a3b8', // Light gray for inactive
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                  <Store />
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary={shop.name}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: location.pathname.startsWith(`/shop/${shop.id}`) ? 600 : 500,
                        color: location.pathname.startsWith(`/shop/${shop.id}`) ? 'inherit' : '#f8fafc',
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      {open && <Divider sx={{ my: 1, mx: 2, borderColor: '#334155' }} />}

      {/* Ustawienia */}
      {open && (
        <>
          <Box sx={{ px: 2, py: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Ustawienia
            </Typography>
          </Box>
          <Divider sx={{ my: 0.5, mx: 2, borderColor: '#334155' }} />
        </>
      )}
      <List>
        <ListItem disablePadding>
          <Tooltip
            title={!open ? 'Ustawienia' : ''}
            placement="right"
            disableHoverListener={open}
          >
                         <ListItemButton
               onClick={() => navigate('/settings')}
               selected={location.pathname === '/settings'}
               sx={{
                 minHeight: 48,
                 justifyContent: open ? 'initial' : 'center',
                 px: open ? 2.5 : 0,
                 mx: open ? 1 : 0,
                 borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: '#6366f1', // Primary color
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#4f46e5', // Darker primary
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'inherit',
                  },
                },
                '&:hover': {
                  backgroundColor: '#334155', // Dark hover
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
                             <ListItemIcon
                 sx={{
                   minWidth: 0,
                   mr: open ? 3 : 0,
                   ml: open ? 0 : 0,
                   justifyContent: 'center',
                   color: location.pathname === '/settings' ? 'inherit' : '#94a3b8', // Light gray for inactive
                   display: 'flex',
                   alignItems: 'center',
                 }}
               >
                <Settings />
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary="Ustawienia"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: location.pathname === '/settings' ? 600 : 500,
                      color: location.pathname === '/settings' ? 'inherit' : '#f8fafc',
                    },
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        <ListItem disablePadding>
          <Tooltip
            title={!open ? 'Utwórz użytkownika' : ''}
            placement="right"
            disableHoverListener={open}
          >
                         <ListItemButton
               onClick={() => navigate('/register')}
               selected={location.pathname === '/register'}
               sx={{
                 minHeight: 48,
                 justifyContent: open ? 'initial' : 'center',
                 px: open ? 2.5 : 0,
                 mx: open ? 1 : 0,
                 borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: '#6366f1', // Primary color
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#4f46e5', // Darker primary
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'inherit',
                  },
                },
                '&:hover': {
                  backgroundColor: '#334155', // Dark hover
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
                             <ListItemIcon
                 sx={{
                   minWidth: 0,
                   mr: open ? 3 : 0,
                   ml: open ? 0 : 0,
                   justifyContent: 'center',
                   color: location.pathname === '/register' ? 'inherit' : '#94a3b8', // Light gray for inactive
                   display: 'flex',
                   alignItems: 'center',
                 }}
               >
                <PersonAdd />
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary="Utwórz użytkownika"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: location.pathname === '/register' ? 600 : 500,
                      color: location.pathname === '/register' ? 'inherit' : '#f8fafc',
                    },
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        <ListItem disablePadding>
          <Tooltip
            title={!open ? 'Sklepy' : ''}
            placement="right"
            disableHoverListener={open}
          >
                         <ListItemButton
               onClick={() => navigate('/shops')}
               selected={location.pathname === '/shops'}
               sx={{
                 minHeight: 48,
                 justifyContent: open ? 'initial' : 'center',
                 px: open ? 2.5 : 0,
                 mx: open ? 1 : 0,
                 borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: '#6366f1', // Primary color
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#4f46e5', // Darker primary
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'inherit',
                  },
                },
                '&:hover': {
                  backgroundColor: '#334155', // Dark hover
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
                             <ListItemIcon
                 sx={{
                   minWidth: 0,
                   mr: open ? 3 : 0,
                   ml: open ? 0 : 0,
                   justifyContent: 'center',
                   color: location.pathname === '/shops' ? 'inherit' : '#94a3b8', // Light gray for inactive
                   display: 'flex',
                   alignItems: 'center',
                 }}
               >
                <Store />
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary="Sklepy"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: location.pathname === '/shops' ? 600 : 500,
                      color: location.pathname === '/shops' ? 'inherit' : '#f8fafc',
                    },
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>

             {/* User Section */}
       <Box sx={{ mt: 'auto', p: 2 }}>
         {open && <Divider sx={{ mb: 2, mx: 1, borderColor: '#334155' }} />}
                  {renderExpandableItem(
            {
              text: 'Użytkownik',
              icon: <AccountCircle />,
              badge: null
            },
            profileOpen,
            () => setProfileOpen(!profileOpen)
          )}
        <Collapse in={profileOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton sx={{ pl: 4, mx: 1, borderRadius: 2 }}>
              <ListItemIcon sx={{ minWidth: 36, color: '#94a3b8' }}>
                <Person />
              </ListItemIcon>
              <ListItemText 
                primary="Mój profil" 
                sx={{ 
                  '& .MuiListItemText-primary': {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#f8fafc',
                  }
                }}
              />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4, mx: 1, borderRadius: 2 }}
              onClick={() => logout()}
            >
              <ListItemIcon sx={{ minWidth: 36, color: '#94a3b8' }}>
                <Logout />
              </ListItemIcon>
              <ListItemText 
                primary="Wyloguj" 
                sx={{ 
                  '& .MuiListItemText-primary': {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#f8fafc',
                  }
                }}
              />
            </ListItemButton>
          </List>
        </Collapse>
      </Box>
    </Drawer>
  );
};

export default Sidebar; 